import { Link } from "react-router-dom";

import type { Invitation } from "../lib/api";
import {
  DASH,
  fmtInt,
  isPass,
  roleWords,
  shortDate,
  useDHDistrict,
  useDHGodowns,
  useDHInvitations,
  useDHReports,
  useMe,
} from "./data";
import { Tray } from "../statehead/Tray";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/** One activity row from an invitation the district head sent to a QC
 * agent. */
function inviteTick(inv: Invitation) {
  if (inv.accepted_at) {
    return {
      date: shortDate(inv.accepted_at),
      title: `${inv.email} accepted the invite`,
      tag: "tag-live",
      label: "Invite accepted",
    };
  }
  if (inv.revoked_at) {
    return {
      date: shortDate(inv.revoked_at),
      title: `Invite to ${inv.email} revoked`,
      tag: "tag-off",
      label: "Revoked",
    };
  }
  return {
    date: shortDate(inv.created_at),
    title: `Invite sent to ${inv.email}`,
    tag: "tag-setup",
    label: "Invite sent",
  };
}

export function Overview() {
  const meQ = useMe();
  const districtQ = useDHDistrict();
  const godownsQ = useDHGodowns();
  const reportsQ = useDHReports();
  const invitesQ = useDHInvitations();

  const firstName = meQ.data?.user.first_name;
  const district = districtQ.data?.district;
  const godowns = godownsQ.data?.godowns ?? [];
  const reports = reportsQ.data?.reports ?? [];
  const invites = (invitesQ.data?.invitations ?? []).filter(
    (i) => i.role === "godown_qc_agent",
  );

  const places = new Set(
    godowns.map((g) => (g.place ?? "").trim()).filter(Boolean),
  );
  const passed = reports.filter((r) => isPass(r.verdict)).length;
  const passRate = reports.length
    ? Math.round((passed / reports.length) * 100)
    : null;

  const byGodown = godowns.map((g) => {
    const rs = reports.filter((r) => r.godown_id === g.id);
    return {
      godown: g,
      passed: rs.filter((r) => isPass(r.verdict)).length,
      total: rs.length,
    };
  });

  return (
    <>
      <p className="crumb">
        {(district?.name ?? "DISTRICT").toUpperCase()} / DISTRICT HEAD
      </p>
      <div className="page-head">
        <div>
          <h1>
            {greeting()}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p>
            Grain grading and reports across your godowns, updated as field
            devices file them.
          </p>
        </div>
        <div className="page-actions">
          <Link className="btn btn-gold" to="/dh/godowns">
            Add godown
          </Link>
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <span>GODOWNS</span>
          <b>{godownsQ.isLoading ? DASH : fmtInt(godowns.length)}</b>
          <i>in {district?.name ?? "your district"}</i>
        </div>
        <div className="stat">
          <span>PLACES</span>
          <b>{godownsQ.isLoading ? DASH : fmtInt(places.size)}</b>
          <i>covered by your godowns</i>
        </div>
        <div className="stat">
          <span>REPORTS</span>
          <b>{reportsQ.isLoading ? DASH : fmtInt(reports.length)}</b>
          {passRate != null ? (
            <i className="up">
              {fmtInt(passed)} passed · {passRate}%
            </i>
          ) : (
            <i>{DASH}</i>
          )}
        </div>
        <div className="stat">
          <span>MEDIAN TURNAROUND</span>
          <b>{DASH}</b>
          <i>not tracked yet</i>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Reports by godown</h2>
            <p>Tray fill shows reports passed out of reports filed.</p>
          </div>
          <div className="right">
            <Link className="btn" to="/dh/godowns">
              Open godowns
            </Link>
          </div>
        </div>
        <div className="card-body">
          {godownsQ.isLoading && <div className="tray-cap">Loading…</div>}
          {!godownsQ.isLoading && byGodown.length === 0 && (
            <div className="empty" style={{ border: 0 }}>
              <b>No godowns yet</b>
              <p>
                Add your first godown and invite its QC agent to start seeing
                reports here.
              </p>
              <Link className="btn btn-gold" to="/dh/godowns">
                Add godown
              </Link>
            </div>
          )}
          {byGodown.map((row) => (
            <div className="tk-row" key={row.godown.id}>
              <span className="dname">
                {row.godown.name}
                <em>
                  {(row.godown.place ?? "").trim() || "PLACE NOT SET"}
                </em>
              </span>
              <span>
                <Tray passed={row.passed} total={row.total} />
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Recent activity</h2>
            <p>Invitations you've sent to godown QC agents.</p>
          </div>
        </div>
        {invites.length === 0 ? (
          <div className="card-body">
            <div className="empty" style={{ border: 0 }}>
              <b>Nothing yet</b>
              <p>
                Invitations you send to QC agents will show up here as they're
                sent and accepted.
              </p>
            </div>
          </div>
        ) : (
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {invites.map((inv) => {
              const t = inviteTick(inv);
              return (
                <div className="tick" key={inv.id}>
                  <span className="tick-id">{t.date}</span>
                  <div>
                    <b>{t.title}</b>
                    <p>
                      {roleWords(inv.role)} · {inv.target_scope_type} #
                      {inv.target_scope_id}
                    </p>
                  </div>
                  <span className={"tag " + t.tag}>
                    <span className="dot" />
                    {t.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
